import * as types from './mutation-types'
import actions from './actions'
import { bootstrap } from '../../../utils/vuex'
import {
  parseFieldDependencies,
  parsePath,
  parseJsonHead,
  isObject,
  hasValue,
  isSubFormPath,
  ERROR_MSG_TYPE,
  PATH_SEPARATOR,
} from '../../../utils'
import { camelCase, get, filter, isEmpty, isNil, isUndefined, set, isNull } from 'lodash'
import { prepareQueries } from './helpers/init-queries'
import Vue from 'vue'
import { decodeHTML } from 'entities'
import { isDocumentProp, isInfo, isMap, isNumber, isRichText, isSubForm } from '../../../form'
import { uuidv4 } from '../../../components/query-builder/helpers/helpers'
import { SOURCE_TYPES } from '../../../components/list-new/lib/list-constants'
import EventBus from '../../../utils/event-bus'
import { FIELD_PROPS } from '../../../consts'

/**
 *
 * @param data
 * @param externalParams
 * @return {{lockUserName, lockTime: (boolean|*), isLocked, lockUserEmail: (string|*)}}
 */
const setLockInfo = (data, externalParams) => {
  const lock = {
    isLocked: data.isLocked,
    lockUserName: data.userName,
    lockUserEmail: data.userEMail,
    lockTime: data.lockValid,
  }

  if (data.isLocked) {
    if (data.userID == externalParams.ID_S_USER) {
      lock.isLocked = false
    } else {
      lock.isLocked = true
    }
  }

  return lock
}

/**
 * @description Returns the namespaced formstore's state
 * @returns {{dirty: boolean, isCloning: boolean, formStructure: {}, version: null, formCaptcha: boolean, idMainTable: null, idForm: null, fieldPathMap: {}, form: {}, newElementKey: string, externalParams: {}, formLock: {lockUserName: null, lockTime: null, isLocked: boolean, lockUserEmail: null}, showNotiOnExp: {}, storeNamespace: string, popupUrl: string, expressionChain: {}, messages: *[], inPopup: boolean, captchaFieldPath: boolean, formData: {}, captchaText: boolean, lockKey: null, captchaValue: boolean, formPageIndex: number}}
 */
const state = function () {
  return {
    idForm: null,
    idMainTable: null,
    url: null,
    externalParams: {},
    form: {},
    formData: {},
    formStructure: {},
    messages: [],
    activePageId: 'page0',
    formLock: {
      isLocked: false,
      lockUserName: null,
      lockUserEmail: null,
      lockTime: null,
    },
    dirty: false,
    lockKey: null,
    version: null,
    isCloning: false,
    inPopup: false,
    popupUrl: '',
    newElementKey: '',
    showNotiOnExp: {},
    storeNamespace: '',
    captchaText: false,
    captchaValue: false,
    captchaFieldPath: false,
    formCaptcha: false,
    expressionChain: {},
    fieldPathMap: {},
    fieldPathMapByFormName: {},
    fieldsToSkip: [],
    triggerNames: {},
  }
}

const getters = {
  idForm: (state) => state.idForm,
  idMainTable: (state) => state.idMainTable,
  externalParams: (state) => state.externalParams,
  form: (state) => state.form,
  url: (state) => state.url,
  isReadView: (state) => state.isReadView,
  showHistory: (state) => state.showHistory,
  subformView: (state) => state.subformView,
  submitRoot: (state) => state.submitRoot,
  groupAutoCollapse: (state) => state.groupAutoCollapse,
  collapsedDefaultClose: (state) => state.collapsedDefaultClose,
  usernameHidden: (state) => state.usernameHidden,
  messages: (state) => state.messages,
  formLock: (state) => state.formLock,
  formData: (state) => state.formData,
  getFormData:
    (state) =>
    (useFieldName = false) => {
      let transformedData = {}
      state.form.pages.forEach((page) => {
        page.groups.forEach((group) => {
          group.rows.forEach((row) => {
            if (row) {
              row.forEach((field) => {
                const fieldName = useFieldName ? camelCase(field.name) : field.id
                transformedData[fieldName] = ''
                if (isSubForm(field)) {
                  if (field.subForm && field.subForm.rows && field.subForm.rows.length) {
                    transformedData[fieldName] = field.subForm.rows.reduce((acc, row) => {
                      let rowData = row.items.reduce((accumulator, item) => {
                        if (!isUndefined(item.value)) {
                          accumulator[item.id.replace(/-.*/g, '')] = item.value
                        } else {
                          accumulator[item.id.replace(/-.*/g, '')] = ''
                        }
                        return accumulator
                      }, {})
                      acc.push(rowData)
                      return acc
                    }, [])
                  } else {
                    transformedData[fieldName] = []
                  }
                } else if (isMap(field)) {
                  if (field.value) {
                    transformedData[fieldName] = JSON.stringify(field.value)
                  }
                } else if (isDocumentProp(field)) {
                  if (field.value && field.value.length) {
                    transformedData[fieldName] = field.value[0].id
                  }
                } else if (isRichText(field) && !isNil(field.value)) {
                  transformedData[fieldName] = decodeHTML(field.value)
                } else if (!isNil(field.value)) {
                  if (isNumber(field)) {
                    transformedData[fieldName] = String(field.value)
                  } else if (isInfo(field)) {
                    transformedData[fieldName] = ''
                  } else {
                    transformedData[fieldName] = field.value
                  }
                } else if (isNull(field.value)) {
                  transformedData[fieldName] = null
                }
              })
            }
          })
        })
      })
      return transformedData
    },
  getSubFormData: (state, getters) => (path) => {
    const subFormData = getters.fieldByPath(path).subForm
    const transformedData = { [subFormData.id]: [] }
    if (subFormData.rows && subFormData.rows.length) {
      subFormData.rows.forEach((row) => {
        let rowData = {}
        row.items.forEach((item) => {
          if (!isUndefined(item.value)) {
            rowData[item.id.replace(/-.*/g, '')] = item.value
          } else {
            rowData[item.id.replace(/-.*/g, '')] = ''
          }
        })
        transformedData[subFormData.id].push(rowData)
      })
    }
    return transformedData
  },
  getListExpressionContextData: (state, getters) => (path) => {
    const field = getters.expressionFieldByPath(path)
    const rowId = get(path.split('/'), 4)
    const expressionData = {}
    const expression = get(field, ['listAst', 'exp'], get(field, ['filterExp', 'exp'], get(field, ['listExp', 'exp'])))
    let dependencies = []
    if (expression) {
      dependencies = parseFieldDependencies(expression)
    }
    if (dependencies.length) {
      for (const { fieldName, prop } of dependencies) {
        let path = get(getters.fieldPathMap, fieldName)
        if (!isUndefined(rowId)) path = path.replace('rowId', rowId)
        const relatedField = getters.expressionFieldByPath(path)
        const relatedFieldValue = get(relatedField, prop)
        set(expressionData, [relatedField.name, prop], hasValue(relatedFieldValue) ? relatedFieldValue : null)
      }
    }
    return isEmpty(expressionData) ? null : JSON.stringify(expressionData)
  },
  formStructure: (state) => state.formStructure,
  formPageById: (state) => (pageId) => {
    const page = get(state, `form.pages`, []).find(({ id }) => id === pageId)
    return page ? page : {}
  },
  groupsByPageIndex: (state) => (pageIndex) => get(state, `form.pages[${pageIndex}].groups`, []),
  groupsByPageId: (state) => (pageId) => {
    const page = get(state, 'form.pages', []).find(({ id }) => id === pageId)
    return page ? page.groups : []
  },
  groupByPath: (state) => (path) => {
    const [pageIndex, groupIndex] = parsePath(path)
    return get(state, `form.pages[${pageIndex}].groups[${groupIndex}]`, {})
  },
  subFormRowPrimaryInListValue: (state) => (path) => {
    let [pageId, groupId, rowId, fieldId, sfRowId] = parsePath(path)
    const primaryInList = get(
      state,
      `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subFormBuilderData.primaryInList`,
      null
    )
    const subform = get(state, `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`, {})
    const rowItems = get(subform, `rows[${sfRowId}].items`, [])
    const rowItem = primaryInList ? rowItems.find((item) => item.name === primaryInList) : rowItems[0]
    let returnValue = rowItem.formattedValue
    if (!returnValue) {
      const rowItemList = get(rowItem, 'list.options', [])
      const findListItem = rowItemList.find((item) => item.key === rowItem.value)
      returnValue = findListItem ? findListItem.value : rowItem.value
    }
    return returnValue
  },
  rowByPath: (state) => (path) => {
    const [pageIndex, groupIndex, rowIndex] = parsePath(path)
    return get(state, `form.pages[${pageIndex}].groups[${groupIndex}].rows[${rowIndex}]`, {})
  },
  fieldByPath: (state) => (path) => {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    return get(state, `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}]`, {})
  },
  idFormByFieldPath: (state) => (path) => {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    if (isSubFormPath(path)) {
      return get(state, ['form', 'pages', pageId, 'groups', groupId, 'rows', rowId, fieldId, 'subIdForm'])
    } else {
      return get(state, 'idForm')
    }
  },
  expressionFieldByPath: (state) => (path) => {
    if (path === 'form') return state.form
    let fieldPath = 'form'
    let [pageId, groupId, rowId, fieldId, sfRowIndex, sfItemIndex] = parsePath(path)

    if (pageId) fieldPath += `.pages[${pageId}]`
    if (groupId) fieldPath += `.groups[${groupId}]`
    if (rowId && fieldId) fieldPath += `.rows[${rowId}][${fieldId}]`
    let field = get(state, fieldPath)

    if (!isNaN(parseInt(sfRowIndex)) && sfItemIndex)
      return get(field, `subForm.rows[${sfRowIndex}].items[${sfItemIndex}]`, {})
    return field
  },
  subFormByPath: (state) => (path) => {
    const [pageIndex, groupIndex, rowIndex, fieldIndex] = parsePath(path)
    return get(state, `form.pages[${pageIndex}].groups[${groupIndex}].rows[${rowIndex}][${fieldIndex}].subForm`, {})
  },
  subFormRowByPath: (state) => (path) => {
    const [pageIndex, groupIndex, rowIndex, fieldIndex, sfRowIndex] = parsePath(path)
    const field = get(state, `form.pages[${pageIndex}].groups[${groupIndex}].rows[${rowIndex}][${fieldIndex}]`, {})
    return get(field, `subForm.rows[${sfRowIndex}]`, {})
  },
  subFormFieldByPath: (state) => (path) => {
    const [pageIndex, groupIndex, rowIndex, fieldIndex, sfRowIndex, sfItemIndex] = parsePath(path)
    const field = get(state, `form.pages[${pageIndex}].groups[${groupIndex}].rows[${rowIndex}][${fieldIndex}]`, {})
    return get(field, `subForm.rows[${sfRowIndex}].items[${sfItemIndex}]`, {})
  },
  dirty: (state) => state.dirty,
  activePageId: (state) => state.activePageId,
  version: (state) => state.version,
  isCloning: (state) => state.isCloning,
  inPopup: (state) => state.inPopup,
  popupUrl: (state) => state.popupUrl,
  newElementKey: (state) => state.newElementKey,
  showNotiOnExp: (state) => state.showNotiOnExp,
  isDocumentUploadInProgress: (state) => state.isDocumentUploadInProgress,
  storeNamespace: (state) => state.storeNamespace,
  captchaText: (state) => state.captchaText,
  captchaValue: (state) => state.captchaValue,
  captchaFieldPath: (state) => state.captchaFieldPath,
  formCaptcha: (state) => state.formCaptcha,
  subFormExpressions: (state) => state.subFormExpressions,
  expressionChain: (state) => state.expressionChain,
  fieldPathMap: (state) => state.fieldPathMap,
  fieldPathMapByFormName: (state) => state.fieldPathMapByFormName,
  sourceType: () => SOURCE_TYPES.FORM,
  idSource: (state) => (path) => {
    if (path.split(PATH_SEPARATOR).length > 4) {
      const fieldGetter = getters.fieldByPath(state)
      return fieldGetter(path).subIdForm
    } else {
      return state.idForm
    }
  },
  visiblePages: (state) => get(state, 'form.pages', []).filter(({ hidden, invisible }) => !hidden && !invisible),
}

const mutations = {
  [types.SET_NAMESPACE](state, storeNamespace) {
    Vue.set(state, 'storeNamespace', storeNamespace)
  },
  [types.FORM_SET_POPUP_URL](state, popupUrl) {
    Vue.set(state, 'popupUrl', popupUrl)
  },
  [types.FORM_SET_IN_POPUP](state, inPopup) {
    Vue.set(state, 'inPopup', inPopup)
  },
  [types.FORM_SET_DIRTY](state, { isDirty }) {
    Vue.set(state, 'dirty', isDirty)
  },
  [types.FORM_SET_CLONING](state, isCloning) {
    Vue.set(state, 'isCloning', isCloning)
  },
  [types.FORM_SET_READONLY](state, readOnly) {
    Vue.set(state.form, 'readOnly', readOnly)
  },
  [types.FORM_SET_VERSION](state, version) {
    Vue.set(state, 'version', version)
  },
  [types.FORM_SET_DOCUMENT_UPLOAD_IN_PROGRESS](state, isDocumentUploadInProgress) {
    Vue.set(state, 'isDocumentUploadInProgress', isDocumentUploadInProgress)
  },
  [types.FIELD_UPDATE_VALUE](state, { key, value }) {
    Vue.set(state.form[key], 'value', value)
  },
  [types.FORM_FETCH_STRUCTURE](state, formStructure) {
    Vue.set(state, 'formStructure', formStructure)
    Vue.set(state, 'expressionChain', formStructure.expressionChain)
    Vue.set(state, 'subFormExpressions', formStructure.subFormExpressions)
    Vue.set(state, 'fieldPathMap', formStructure.fieldPathMap)
    Vue.set(state, 'fieldPathMapByFormName', formStructure.fieldPathMapByFormName)
  },
  [types.OVERWRITE_DATA](state, newData) {
    Object.entries(newData).forEach((item) => {
      if (state.formData.ITEMS) {
        const fieldData = state.formData.ITEMS[item[0]]
        if (fieldData) {
          Vue.set(state.formData.ITEMS, item[0], item[1])
        } else {
          state.formData.ITEMS = Object.assign({}, state.formData.ITEMS, { [item[0]]: item[1] })
        }
      }
    })
  },
  [types.FORM_FETCH_DATA](state, data) {
    Vue.set(state, 'formData', data)
  },
  async [types.FORM_LOAD](state, { rootState, formData }) {
    Vue.set(state, 'form', formData)
    await prepareQueries({ state, rootState })
  },
  [types.FORM_INIT](
    state,
    {
      idForm,
      idMainTable,
      externalParams,
      showNotiOnExp,
      isReadView,
      showHistory,
      subformView,
      submitRoot,
      usernameHidden,
      groupAutoCollapse,
      collapsedDefaultClose,
    }
  ) {
    Vue.set(state, 'idForm', idForm)
    Vue.set(state, 'idMainTable', idMainTable)
    Vue.set(state, 'externalParams', externalParams)
    Vue.set(state, 'showNotiOnExp', showNotiOnExp)
    Vue.set(state, 'isReadView', isReadView)
    Vue.set(state, 'showHistory', showHistory)
    Vue.set(state, 'subformView', subformView)
    Vue.set(state, 'submitRoot', submitRoot)
    Vue.set(state, 'usernameHidden', usernameHidden)
    Vue.set(state, 'groupAutoCollapse', groupAutoCollapse)
    Vue.set(state, 'collapsedDefaultClose', collapsedDefaultClose)
  },
  [types.FORM_CLEAR_MAIN_TABLE](state) {
    Vue.set(state, 'idMainTable', null)
    state.form.pages.forEach((page) => {
      page.groups.forEach((group) => {
        group.rows.forEach((row) => {
          if (row) {
            row.forEach((field) => {
              if (field.type == 'subform') {
                if (field.subForm && field.subForm.rows && field.subForm.rows.length) {
                  field.subForm.rows.forEach((row) => {
                    row.items.forEach((sfItem) => {
                      if (sfItem.id == 'ID') {
                        Vue.set(sfItem, 'value', null)
                      }
                    })
                  })
                }
              }
              if (field.id == state.form.idMainTableField) {
                Vue.set(field, 'value', '')
              }
            })
          }
        })
      })
    })
  },
  [types.FORM_UPDATE_FIELD_VALUE](state, { path, value }) {
    Vue.set(state.form.pages[path.pageId].groups[path.groupId].rows[path.rowId][path.fieldId], 'value', value)
  },
  [types.FORM_UPDATE_GROUP_VALUE](state, { path, key, value }) {
    let [pageId, groupId] = parsePath(path)
    Vue.set(state.form.pages[pageId].groups[groupId], key, value)
  },
  [types.FORM_UPDATE_PAGE_VALUE](state, { path, key, value }) {
    let pageId = path
    Vue.set(state.form.pages[pageId], key, value)
  },
  [types.FORM_UPDATE_FIELD_REQUIRED](state, { path, value }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    if (state.form.pages[pageId].groups[groupId].rows[rowId][fieldId].validations) {
      Vue.set(state.form.pages[pageId].groups[groupId].rows[rowId][fieldId].validations, 'required', value)
    } else {
      Vue.set(state.form.pages[pageId].groups[groupId].rows[rowId][fieldId], 'validations', { required: value })
    }
  },
  [types.FORM_UPDATE_FIELD_DOCPROP_TITLE](state, { path, idx, value }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    let val = state.form.pages[pageId].groups[groupId].rows[rowId][fieldId].value
    Vue.set(val[idx].title, 'value', value)
  },
  [types.FORM_UPDATE_FIELD_DOCPROP_DESC](state, { path, idx, value }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    let val = state.form.pages[pageId].groups[groupId].rows[rowId][fieldId].value
    Vue.set(val[idx].desc, 'value', value)
  },
  [types.FORM_UPDATE_SUBFORM_BY_PATH](state, { path, data }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const sfPath = `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`
    const subForm = get(state, sfPath)
    if (subForm) {
      Vue.set(subForm, 'rows', data)
    }
  },
  [types.FORM_UPDATE_SUBFORM_ROW_BY_PATH](state, { path, rowIndex, data }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const rowsPath = `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm.rows`
    const subFormRows = get(state, rowsPath)
    if (subFormRows) {
      Vue.set(subFormRows, rowIndex, data)
    }
  },
  [types.FORM_UPDATE_SUBFORM_CLEAR](state, { path }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const sfPath = `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`
    const subForm = get(state, sfPath)
    if (subForm) {
      Vue.set(subForm, 'rows', [])
    }
  },
  [types.FORM_SAVE_DATA](state, { data }) {
    Vue.set(state, 'dirty', false)
    if (isObject(data) && data.HEADER) {
      let messages = parseJsonHead(data.HEADER)
      messages = messages.filter((message) => message.type !== ERROR_MSG_TYPE.SUCCESS)
      Vue.set(state, 'messages', messages)
    }
  },
  [types.FORM_VERIFICATION_DATA](state, { data }) {
    Vue.set(state, 'dirty', false)
    if (isObject(data) && data.HEADER) {
      const messages = parseJsonHead(data.HEADER).map((msg) => ({
        ...msg,
        type: msg.type.replace('success', 'success-verification'),
      }))
      Vue.set(state, 'messages', messages)
    }
  },
  [types.FORM_VEE_VALIDATION](state, { veeValidationErrors }) {
    Vue.set(state, 'dirty', false)
    Vue.set(state, 'messages', veeValidationErrors)
  },
  [types.FORM_REMOVE_ERROR_MESSAGE](state, error) {
    const newMessages = filter(state.messages, (message) => {
      if (message) return message.name !== error.name
      return false
    })
    Vue.set(state, 'messages', newMessages)
  },
  [types.FORM_LOCK_INFO](state, { data }) {
    const formLock = setLockInfo(data, state.externalParams)
    Vue.set(state, 'formLock', formLock)
  },
  [types.FORM_REFRESH](state, { form }) {
    Vue.set(state, 'form', form)
  },
  [types.SET_ACTIVE_PAGE_ID](state, { activePageId }) {
    Vue.set(state, 'activePageId', activePageId)
  },
  [types.FORM_UPDATE_READONLY](state, { readOnly }) {
    Vue.set(state.form, 'readOnly', readOnly)
  },
  [types.FORM_UPDATE_PROP](state, { key, value }) {
    Vue.set(state.form, key, value)
  },
  [types.NEW_ELEMENT_KEY](state, newElementKey) {
    Vue.set(state, 'newElementKey', newElementKey)
  },
  [types.SET_CAPTCHA_TEXT](state, { captchaText }) {
    Vue.set(state, 'captchaText', captchaText)
  },
  [types.SET_CAPTCHA_VALUE](state, { captchaValue }) {
    Vue.set(state, 'captchaValue', captchaValue)
  },
  [types.SET_CAPTCHA_FIELD_PATH](state, { captchaPath }) {
    Vue.set(state, 'captchaFieldPath', captchaPath)
  },
  [types.SET_FORM_CAPTCHA](state, { formCaptcha }) {
    Vue.set(state, 'formCaptcha', formCaptcha)
  },
  [types.ADD_SUB_FORM_ROW](state, { path, row }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const subForm = get(state, `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`, {})
    for (const item of subForm.rows) {
      Vue.set(item, 'collapsed', true)
    }
    row.collapsed = false
    Vue.set(subForm.rows, subForm.rows.length, row)
    EventBus.$emit(`added-sub-form-row-${subForm.id}`, { rowUid: row.rowUid })
  },
  [types.REMOVE_SUB_FORM_ROW](state, { path, rowIndex }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const subForm = get(state, `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`, {})
    const rows = subForm.rows
    rows.splice(rowIndex, 1)
    Vue.set(subForm, 'rows', rows)
  },
  [types.SET_SUB_FORM_ROW_COLLAPSED](state, { path, collapsed }) {
    let [pageId, groupId, rowId, fieldId, sfRowId] = parsePath(path)
    const subForm = get(
      state,
      `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm.rows[${sfRowId}]`,
      {}
    )
    Vue.set(subForm, 'collapsed', collapsed)
  },
  [types.UPDATE_PROP_BY_PATH](state, { fieldPath, prop, value, byExpression, needDebounce = false }) {
    const field = get(state, fieldPath)
    if (prop === FIELD_PROPS.REQUIRED) {
      Vue.set(field[FIELD_PROPS.VALIDATIONS], prop, value)
    } else {
      Vue.set(field, prop, value)
    }
    Vue.set(field, 'needDebounce', needDebounce)
    if (!byExpression) {
      Vue.set(state, 'dirty', true)
    }
  },
  [types.SET_ROW_UID](state, { index, path }) {
    let [pageId, groupId, rowId, fieldId] = parsePath(path)
    const subform = get(state, `form.pages[${pageId}].groups[${groupId}].rows[${rowId}][${fieldId}].subForm`, {})
    const field = get(subform, ['rows', index])
    if (field) {
      Vue.set(field, 'rowUid', uuidv4())
    }
  },
  [types.SET_FIELD_TO_SKIP](state, { fieldName }) {
    const fieldsToSkip = get(state, 'fieldsToSkip')
    if (fieldsToSkip) fieldsToSkip.push(fieldName)
  },
  [types.SET_TRIGGER_NAME](state, { fieldId, triggerName }) {
    Vue.set(state.triggerNames, fieldId, triggerName)
  },
}

export default bootstrap({
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
})
